/**
 * Single source of truth for each page's PRIMARY call to action.
 *
 * The hero CTA, the mid-page CTA, the final conversion band, and the mobile
 * sticky bar must all say the same words and point at the same destination —
 * at every breakpoint. Any drift between them costs conversion and confuses
 * analytics, so all four surfaces read from this file instead of hard-coding
 * their own copy.
 *
 * Rule: never inline a primary CTA label or href in a page component. Add or
 * edit it here, then reference `PRIMARY_CTAS.<surface>`.
 */

import { OFFERS, ROUTES } from "@/content/offers";

export type PrimaryCTA = {
  /** Stable id, also used as the sticky-bar slot id. */
  id: string;
  /** Semantic bucket for funnel reports. */
  kind: "registration" | "strategy_session" | "masterclass";
  /** Exact button text shown on hero, mid-page, final band and sticky bar. */
  label: string;
  /** Exact destination shown on all four surfaces. */
  href: string;
  /** Spoken/aria description used by the sticky bar. */
  action: string;
};

/**
 * Prices and destinations are NOT defined here — they come from
 * `src/content/offers.ts`, the single source of truth. Update them there.
 */
export const MASTERCLASS_PRICE = OFFERS.masterclass.priceUsd as number;
export const MASTERCLASS_BOOK_URL = OFFERS.masterclass.href;

export const PRIMARY_CTAS = {
  /** Homepage — the free Make Change Scan is the top-of-funnel entry point. */
  home: {
    id: "primary_cta_home",
    kind: "registration",
    label: "Start Your Free Make Change Scan",
    href: ROUTES.scan,
    action:
      "Start Your Free Make Change Scan. Opens the free Make Change opportunity scan.",
  },
  /** Masterclass — the paid seat is the page's only conversion goal. */
  masterclass: {
    id: "primary_cta_masterclass",
    kind: "masterclass",
    label: `Reserve My Seat — ${OFFERS.masterclass.priceLabel}`,
    href: MASTERCLASS_BOOK_URL,
    action: `Reserve My Seat for ${OFFERS.masterclass.priceLabel}. Opens secure checkout for the Built On Purpose masterclass.`,
  },

  /** Advocacy funnel — every CTA routes into the discovery-call booker. */
  advocacy: {
    id: "primary_cta_advocacy",
    kind: "strategy_session",
    label: "Book a Discovery Call",
    href: "#book",
    action: "Book a Discovery Call. Jumps to the discovery call booker.",
  },
  /** Strategy session — hero, mid-page and final CTAs all target the form. */
  strategySession: {
    id: "primary_cta_strategy_session",
    kind: "strategy_session",
    label: "Apply for a Strategy Session",
    href: "#apply",
    action:
      "Apply for a Strategy Session. Jumps to the strategy session application form.",
  },
} satisfies Record<string, PrimaryCTA>;

export type PrimaryCTASurface = keyof typeof PRIMARY_CTAS;
